import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import Badge from "../components/ui/Badge";
import {
  Bell,
  ShoppingBag,
  Star,
  Settings,
  Tag,
  CheckCheck,
 
} from "lucide-react";

const TYPE_CONFIG = {
  order: {
    icon: ShoppingBag,
    color: "text-blue-400",
    bg: "bg-blue-500/10",
    border: "border-blue-500/20",
  },
  review: {
    icon: Star,
    color: "text-emerald-400",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/20",
  },
  system: {
    icon: Settings,
    color: "text-violet-400",
    bg: "bg-violet-500/10",
    border: "border-violet-500/20",
  },
  promo: {
    icon: Tag,
    color: "text-amber-400",
    bg: "bg-amber-500/10",
    border: "border-amber-500/20",
  },
};

export default function Notifications() {
  const { user } = useAuth();
  const { getNotifications, markNotificationRead, markAllRead } = useApp();
  const notifications = getNotifications(user.id);
  const unread = notifications.filter((n) => !n.read);

  return (
    <div className="space-y-5 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Notifications
          </h2>
          {unread.length > 0 && (
            <p className="text-xs text-gray-500 mt-0.5">
              {unread.length} unread notification
              {unread.length !== 1 ? "s" : ""}
            </p>
          )}
        </div>
        {unread.length > 0 && (
          <button
            onClick={() => markAllRead(user.id)}
            className="flex items-center gap-1.5 text-xs text-violet-400 hover:text-violet-300 font-medium transition-colors">
            <CheckCheck size={14} /> Mark all read
          </button>
        )}
      </div>

      {/* Notification list */}
      {notifications.length === 0 && (
        <div className="card py-16 flex flex-col items-center justify-center">
          <Bell size={40} className="text-gray-300 dark:text-gray-700 mb-4" />
          <p className="text-sm text-gray-500">
            You're all caught up! No notifications.
          </p>
        </div>
      )}

      {/* Unread section */}
      {unread.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
            Unread
          </p>
          {unread.map((n) => (
            <NotifItem
              key={n.id}
              n={n}
              userId={user.id}
              onRead={markNotificationRead}
            />
          ))}
        </div>
      )}

      {/* Read section */}
      {notifications.filter((n) => n.read).length > 0 && (
        <div className="space-y-2">
          {unread.length > 0 && (
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mt-4">
              Earlier
            </p>
          )}
          {notifications
            .filter((n) => n.read)
            .map((n) => (
              <NotifItem
                key={n.id}
                n={n}
                userId={user.id}
                onRead={markNotificationRead}
              />
            ))}
        </div>
      )}
    </div>
  );
}

function NotifItem({ n, userId, onRead }) {
  const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.system;
  const Icon = cfg.icon;

  return (
    <div
      className={`card p-4 flex items-start gap-4 cursor-pointer transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-700 ${
        !n.read
          ? "border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900"
          : "opacity-70 hover:opacity-100"
      }`}
      onClick={() => !n.read && onRead(userId, n.id)}>
      <div
        className={`w-10 h-10 rounded-xl ${cfg.bg} border ${cfg.border} flex items-center justify-center flex-shrink-0`}>
        <Icon size={18} className={cfg.color} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <p
            className={`text-sm font-semibold ${!n.read ? "text-gray-900 dark:text-gray-100" : "text-gray-500 dark:text-gray-400"}`}>
            {n.title}
          </p>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-[11px] text-gray-600 whitespace-nowrap">
              {n.time}
            </span>
            {!n.read && (
              <span className="w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
            )}
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
          {n.message}
        </p>
        <div className="mt-2">
          <Badge variant={n.type}>
            {n.type.charAt(0).toUpperCase() + n.type.slice(1)}
          </Badge>
        </div>
      </div>
    </div>
  );
}
